<template>
  <div data-testid="grid_sequence_step_list">
    <v-list v-if="exercises && all_video_exercises" subheader class="pb-12">
      <draggable
        v-model="local_value"
        handle=".drag-handle"
        :disabled="readonly"
        :filter="'.no-drag'"
        :prevent-on-filter="false"
        force-fallback="true"
        @end="$emit('dragged')"
      >
        <v-sheet
          v-for="(step, index) in local_value"
          :key="step.id"
          outlined
          rounded
          class="mb-3 grid-sequence-step"
          data-testid="grid_sequence_step_list_step"
        >
          <v-list-item class="drag-handle grid-sequence-step-header" :style="readonly ? '' : 'cursor: move; cursor: grab; user-select: none;'">
            <v-list-item-avatar>
              <v-icon class="grey lighten-1 white--text">drag_indicator</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-if="readonly">{{ step_title(step, index) }}</v-list-item-title>
              <v-text-field
                v-else
                :value="step.title"
                :placeholder="step_fallback(index)"
                class="no-drag grid-sequence-step-title"
                dense
                hide-details
                outlined
                data-testid="grid_sequence_step_list_step_title"
                @click.stop
                @input="set_step_title(index, $event)"
                @mousedown.stop
                @touchstart.stop
              />
            </v-list-item-content>

            <v-list-item-action v-if="!readonly">
              <v-tooltip top open-delay="500">
                <template v-slot:activator="{ on }">
                  <v-btn
                    :class="'no-drag ' + ($vuetify.rtl ? 'ml-n2' : 'mr-n2')"
                    icon
                    outlined
                    v-on="on"
                    @click.stop="duplicate_step(index)"
                  >
                    <v-icon color="purple">mdi-content-duplicate</v-icon>
                  </v-btn>
                </template>
                {{ $t("grid_sequence_step_list.duplicate_step") }}
              </v-tooltip>
            </v-list-item-action>

            <v-list-item-action v-if="!readonly">
              <v-tooltip top open-delay="500">
                <template v-slot:activator="{ on }">
                  <v-btn
                    :class="'no-drag ' + ($vuetify.rtl ? 'ml-n1' : 'mr-n1')"
                    icon
                    outlined
                    v-on="on"
                    @click.stop="remove_step(index)"
                  >
                    <v-icon color="red lighten-1">delete</v-icon>
                  </v-btn>
                </template>
                {{ $t("grid_sequence_step_list.delete_step") }}
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>

          <v-divider />

          <ExerciseListEdit
            :class="$vuetify.rtl ? 'mr-4 mt-1 mb-1' : 'ml-4 mt-1 mb-1'"
            :value="step_exercises(step)"
            :readonly="readonly"
            :additional_video_exercises="additional_video_exercises"
            :show_add_actions="false"
            :manage_items="false"
            @input="set_step_exercises(index, $event)"
            @edit="edit_exercise(index, $event)"
            @duplicate="duplicate_exercise_by_index(index, $event)"
            @delete="remove_exercise_by_index(index, $event)"
            @dragged="$emit('dragged')"
          />

          <v-btn
            v-if="!readonly"
            text
            color="primary"
            class="ml-3 no-drag"
            data-testid="grid_sequence_step_list_add_exercise"
            @click="$emit('add-exercise', index)"
          >
            <v-icon left>add</v-icon>{{ $t("grid_sequence_step_list.add_exercise") }}
          </v-btn>

          <div class="pa-4">
            <h4 class="mb-1">{{ $t("grid_sequence_step_list.transition_conditions") }}</h4>
            <p class="caption grey--text text--darken-1 mb-3">
              {{ $t("grid_sequence_step_list.completion_condition_desc") }}
            </p>
            <GridSequenceCompletionConditionsEdit
              :value="step.completion_conditions"
              :fields="condition_fields(step)"
              :completed_exercises="completed_exercises(step)"
              :steps="local_value"
              :step_id="step.id"
              :readonly="readonly"
              @input="set_conditions(index, $event)"
              @dragged="$emit('dragged')"
            />
          </div>
        </v-sheet>
      </draggable>
    </v-list>

    <div v-if="local_value.length === 0 && !readonly">
      <h3>{{ $t("grid_sequence_step_list.click_to_add_step") }}</h3>
    </div>
    <v-alert v-else-if="local_value.length === 0" outlined type="info">
      {{ $t("grid_sequence_step_list.no_exercises_readonly") }}
    </v-alert>

    <div v-if="!readonly" style="height: 60px; position: relative">
      <v-fab-transition>
        <v-tooltip top open-delay="500">
          <template v-slot:activator="{ on }">
            <v-btn
              color="blue"
              dark
              absolute
              top
              :right="$vuetify.rtl ? false : true"
              :left="$vuetify.rtl ? true : false"
              fab
              v-on="on"
              @click="$emit('add-step')"
              data-testid="grid_sequence_add_step"
            >
              <v-icon>add</v-icon>
            </v-btn>
          </template>
          {{ $t("grid_sequence_step_list.add_step") }}
        </v-tooltip>
      </v-fab-transition>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import draggable from "vuedraggable";
import uuid from "uuid";
import { mapState } from "vuex";
import ExerciseListEdit from "./ExerciseListEdit.vue";
import GridSequenceCompletionConditionsEdit from "./GridSequenceCompletionConditionsEdit.vue";
import {
  alwaysStepCompletionCondition,
  buildConditionFields,
  completedExerciseOptions,
  duplicateSequenceStep,
  isReportableExercise,
  removeExercise as removeSequenceExercise,
  removeStep as removeSequenceStep,
  sequenceExerciseTitle,
  sequenceStepTitle,
  setStepExercises,
} from "../classes/grid_sequence";

export default {
  components: { draggable, ExerciseListEdit, GridSequenceCompletionConditionsEdit },
  props: {
    value: { type: Array, required: true },
    readonly: { type: Boolean, default: false },
    additional_video_exercises: { type: Object, default: () => ({}) },
  },
  computed: {
    ...mapState(["exercises", "video_exercises"]),
    local_value: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    all_video_exercises() {
      return { ...(this.additional_video_exercises || {}), ...(this.video_exercises || {}) };
    },
  },
  methods: {
    step_fallback(index) {
      return this.$t("grid_sequence_step_list.step", { number: index + 1 });
    },
    step_title(step, index) {
      return sequenceStepTitle(step, this.step_fallback(index));
    },
    set_step_title(index, title) {
      Vue.set(this.local_value, index, { ...this.local_value[index], title });
      this.$emit("input", this.local_value);
    },
    step_exercises(step) {
      return step.exercises;
    },
    is_reportable(exercise) {
      return isReportableExercise(exercise);
    },
    condition_fields(step) {
      return buildConditionFields(
        step,
        this.exercises || {},
        this.all_video_exercises || {},
        this.$i18n.lang(),
        this.$t.bind(this),
      );
    },
    completed_exercises(step) {
      return completedExerciseOptions(
        step,
        this.exercises || {},
        this.all_video_exercises || {},
        this.$i18n.lang(),
        this.$t.bind(this),
      );
    },
    exercise_title(exercise) {
      return sequenceExerciseTitle(
        exercise,
        this.exercises || {},
        this.all_video_exercises || {},
        this.$i18n.lang(),
        this.$t.bind(this),
      );
    },
    set_step_exercises(stepIndex, exerciseList) {
      Vue.set(this.local_value, stepIndex, setStepExercises(this.local_value[stepIndex], exerciseList));
      this.$emit("input", this.local_value);
    },
    edit_exercise(stepIndex, exerciseIndex) {
      const exercise = this.step_exercises(this.local_value[stepIndex])[exerciseIndex];
      if (exercise) this.$emit("edit-exercise", stepIndex, exercise.uuid);
    },
    duplicate_exercise_by_index(stepIndex, exerciseIndex) {
      const exercise = this.step_exercises(this.local_value[stepIndex])[exerciseIndex];
      if (exercise) this.duplicate_exercise(stepIndex, exercise.uuid);
    },
    remove_exercise_by_index(stepIndex, exerciseIndex) {
      const exercise = this.step_exercises(this.local_value[stepIndex])[exerciseIndex];
      if (exercise) this.remove_exercise(stepIndex, exercise.uuid);
    },
    set_conditions(index, completion_conditions) {
      const step = { ...this.local_value[index], completion_conditions };
      Vue.set(this.local_value, index, step);
      this.$emit("input", this.local_value);
    },
    remove_step(index) {
      const updated = removeSequenceStep(this.local_value, index);
      this.local_value.splice(0, this.local_value.length, ...updated);
      this.$emit("input", this.local_value);
      this.$emit("deleted", index);
    },
    duplicate_step(index) {
      const step = duplicateSequenceStep(this.local_value[index], uuid.v4);
      this.local_value.splice(index + 1, 0, step);
      this.$emit("input", this.local_value);
      this.$emit("duplicated");
    },
    duplicate_exercise(stepIndex, exerciseId) {
      const step = this.local_value[stepIndex];
      const exercises = [];
      step.exercises.forEach((exercise) => {
        exercises.push(exercise);
        if (exercise.uuid === exerciseId) {
          const copy = JSON.parse(JSON.stringify(exercise));
          copy.uuid = uuid.v4();
          exercises.push(copy);
        }
      });
      Vue.set(this.local_value, stepIndex, { ...step, exercises });
      this.$emit("input", this.local_value);
      this.$emit("duplicated");
    },
    remove_exercise(stepIndex, exerciseId) {
      const step = this.local_value[stepIndex];
      if (step.exercises.length === 1) {
        this.remove_step(stepIndex);
        return;
      }
      const exercise = step.exercises.find((candidate) => candidate.uuid === exerciseId);
      if (
        exercise
        && isReportableExercise(exercise)
        && step.exercises.filter(isReportableExercise).length === 1
      ) {
        messageBus.$emit("error", this.$t("grid_sequence_step_list.cannot_remove_last_reportable"));
        return;
      }
      Vue.set(
        this.local_value,
        stepIndex,
        removeSequenceExercise(step, exerciseId, alwaysStepCompletionCondition()),
      );
      this.$emit("input", this.local_value);
      this.$emit("deleted", stepIndex);
    },
  },
};
</script>

<style scoped>
.grid-sequence-step.v-sheet--outlined {
  border-width: 2px;
  border-color: rgba(33, 150, 243, 0.35);
}

.grid-sequence-step-header {
  background-color: rgba(33, 150, 243, 0.1);
}

.grid-sequence-step-title {
  max-width: 360px;
}

.theme--dark .grid-sequence-step.v-sheet--outlined {
  border-color: rgba(100, 181, 246, 0.5);
}

.theme--dark .grid-sequence-step-header {
  background-color: rgba(100, 181, 246, 0.16);
}
</style>
